import React, { useMemo, useEffect, useReducer } from 'react';
import styles from "./Form30Dashboard.module.css";
import sButtons from "../../assets/styles/buttons.module.css";
import {Form30DashboardFilter} from "../../components/Form30/Form30DashboardFilter/Form30DashboardFilter";
import {Form30DashboardMap} from "../../components/Form30/Form30DashboardMap/Form30DashboardMap";
import {Form30ColorModal} from "../../components/Form30/Form30ColorModal/Form30ColorModal";
import {PhysicalGroupsChart} from "../../components/Form30/PhysicalGroupsChart/PhysicalGroupsChart";
import {HealthGroupsChart} from "../../components/Form30/HealthGroupsChart/HealthGroupsChart";
import {RehabilitationChart} from "../../components/Form30/RehabilitationChart/RehabilitationChart";
import {ChildCountChart} from "../../components/Form30/ChildCountChart/ChildCountChart";
import {MetricsBlock} from "../../components/Form30/MetricsBlock/MetricsBlock";
import {useFetchForm30Dashboard} from "../../hooks/useQuery/useFetchForm30Dashboard";
import {useFetchForm30ColorSettings} from "../../hooks/useQuery/useFetchForm30ColorSettings";
import {useFetchSectorCharts} from "../../hooks/useQuery/useFetchSectorCharts";
import {useFetchForm30UserPreferences} from "../../hooks/useQuery/useFetchForm30UserPreferences";
import {useSaveForm30UserPreferences} from "../../hooks/useQuery/useSaveForm30UserPreferences";
import {FORM30_TABLES} from "../../constants/form30Tables";
import {useFetchMapSectorsInfo} from "../../hooks/useQuery";
import {useFetchRFMetrics} from "../../hooks/useQuery/useFetchRFMetrics";
import {useFetchRFCharts} from "../../hooks/useQuery/useFetchRFCharts";

interface Form30PageProps {

}

// Типы для reducer
type Form30State = {
    selectedYear: number | null;
    selectedIndicator: string | null;
    appliedYear: number | null;
    appliedIndicator: string | null;
    selectedSectorId: string | null;
    showColorModal: boolean;
    isInitialized: boolean;
    selectedMetricsBlock: number | null;
};

type Form30Action =
    | { type: 'SET_SELECTED_YEAR'; payload: number | null }
    | { type: 'SET_SELECTED_INDICATOR'; payload: string | null }
    | { type: 'SET_APPLIED_YEAR'; payload: number | null }
    | { type: 'SET_APPLIED_INDICATOR'; payload: string | null }
    | { type: 'SET_SELECTED_SECTOR_ID'; payload: string | null }
    | { type: 'SET_SHOW_COLOR_MODAL'; payload: boolean }
    | { type: 'SET_IS_INITIALIZED'; payload: boolean }
    | { type: 'SET_SELECTED_METRICS_BLOCK'; payload: number | null }
    | { type: 'INITIALIZE_FROM_PREFERENCES'; payload: { year: number; indicatorString: string } }
    | { type: 'APPLY_FILTERS'; payload: { year: number | null; indicator: string | null } };

const initialState: Form30State = {
    selectedYear: new Date().getFullYear(),
    selectedIndicator: null,
    appliedYear: null,
    appliedIndicator: null,
    selectedSectorId: null,
    showColorModal: false,
    isInitialized: false,
    selectedMetricsBlock: null,
};

function form30Reducer(state: Form30State, action: Form30Action): Form30State {
    switch (action.type) {
        case 'SET_SELECTED_YEAR':
            return { ...state, selectedYear: action.payload };
        case 'SET_SELECTED_INDICATOR':
            return { ...state, selectedIndicator: action.payload };
        case 'SET_APPLIED_YEAR':
            return { ...state, appliedYear: action.payload };
        case 'SET_APPLIED_INDICATOR':
            return { ...state, appliedIndicator: action.payload };
        case 'SET_SELECTED_SECTOR_ID':
            return { ...state, selectedSectorId: action.payload };
        case 'SET_SHOW_COLOR_MODAL':
            return { ...state, showColorModal: action.payload };
        case 'SET_IS_INITIALIZED':
            return { ...state, isInitialized: action.payload };
        case 'SET_SELECTED_METRICS_BLOCK':
            return { ...state, selectedMetricsBlock: action.payload };
        case 'INITIALIZE_FROM_PREFERENCES':
            return {
                ...state,
                selectedYear: action.payload.year,
                selectedIndicator: action.payload.indicatorString,
                appliedYear: action.payload.year,
                appliedIndicator: action.payload.indicatorString,
                isInitialized: true,
            };
        case 'APPLY_FILTERS':
            return {
                ...state,
                appliedYear: action.payload.year,
                appliedIndicator: action.payload.indicator,
            };
        default:
            return state;
    }
}

const Form30DashboardPage: React.FC<Form30PageProps> = () => {
    const [state, dispatch] = useReducer(form30Reducer, initialState);
    const {
        selectedYear,
        selectedIndicator,
        appliedYear,
        appliedIndicator,
        selectedSectorId,
        showColorModal,
        isInitialized,
        selectedMetricsBlock,
    } = state;

    // Загружаем сохраненные предпочтения пользователя
    const { userPreferences } = useFetchForm30UserPreferences();
    const { savePreferences } = useSaveForm30UserPreferences();

    // Инициализация фильтров из сохраненных предпочтений
    useEffect(() => {
        if (userPreferences && !isInitialized) {
            const { year, table_id, row_id } = userPreferences;
            const indicatorString = `${table_id}_${row_id}`;
            
            dispatch({
                type: 'INITIALIZE_FROM_PREFERENCES',
                payload: { year, indicatorString }
            });
        }
    }, [userPreferences, isInitialized]);

    // Парсим выбранный показатель для получения table_id, row_id и column_key
    const parsedIndicator = useMemo(() => {
        if (!appliedIndicator) return null;
        
        // Формат: table_id_row_id (например: child_count_row_1_1_1)
        // Ищем таблицу, ID которой является началом строки
        const table = FORM30_TABLES.find(t => appliedIndicator.startsWith(t.id + '_'));
        if (!table) return null;
        
        // Извлекаем row_id (всё после table_id + '_')
        const row_id = appliedIndicator.substring(table.id.length + 1);
        
        const row = table.rows.find(r => r.id === row_id);
        if (!row) return null;
        
        // Берем первую колонку с данными (не 'number' и не 'indicator')
        const dataColumn = table.columns.find(col => 
            col.type === 'number' && col.key !== 'number'
        );
        
        if (!dataColumn) return null;
        
        return {
            table_id: table.id,
            row_id,
            column_key: dataColumn.key,
            tableName: table.title,
            rowName: `${row.number} ${row.indicator}`
        };
    }, [appliedIndicator]);

    const handleApplyFilters = () => {
        dispatch({
            type: 'APPLY_FILTERS',
            payload: { year: selectedYear, indicator: selectedIndicator }
        });

        // Парсим selectedIndicator напрямую для сохранения
        if (selectedYear && selectedIndicator) {
            const table = FORM30_TABLES.find(t => selectedIndicator.startsWith(t.id + '_'));
            if (table) {
                const row_id = selectedIndicator.substring(table.id.length + 1);
                const row = table.rows.find(r => r.id === row_id);
                const dataColumn = table.columns.find(col => 
                    col.type === 'number' && col.key !== 'number'
                );
                
                if (row && dataColumn) {
                    savePreferences({
                        year: selectedYear,
                        table_id: table.id,
                        row_id,
                        column_key: dataColumn.key
                    });
                }
            }
        }
    };

    // Получаем данные о секторах
    const { mapSectorsInfoData } = useFetchMapSectorsInfo();

    // Запрос данных для дашборда
    const { dashboardData, isLoading, isError } = useFetchForm30Dashboard({
        year: appliedYear,
        table_id: parsedIndicator?.table_id || null,
        row_id: parsedIndicator?.row_id || null,
        column_key: parsedIndicator?.column_key || null
    });

    // Запрос настроек цветов
    const { colorSettings } = useFetchForm30ColorSettings({
        year: appliedYear,
        table_id: parsedIndicator?.table_id || null,
        row_id: parsedIndicator?.row_id || null,
        column_key: parsedIndicator?.column_key || null
    });

    // Дефолтные цвета, если не настроены
    const defaultColors = [
        { minValue: 0, maxValue: 50, color: '#ffcccc' },
        { minValue: 50, maxValue: 100, color: '#ff9999' },
        { minValue: 100, maxValue: 200, color: '#ff6666' },
        { minValue: 200, maxValue: 500, color: '#ff3333' },
        { minValue: 500, maxValue: 10000, color: '#cc0000' }
    ];

    const currentColors = colorSettings?.colors || defaultColors;

    // Адаптируем данные для карты
    const adaptedDashboardData = useMemo(() => {
        if (!dashboardData || dashboardData.length === 0) return [];
        
        return dashboardData.map(item => ({
            _id: item.sector_id,
            value: item.value
        }));
    }, [dashboardData]);

    // Запрос средних данных по РФ
    const { rfMetricsData } = useFetchRFMetrics({
        year: appliedYear,
        enabled: !!appliedYear && !selectedSectorId
    });

    const { 
        physicalGroupsData: rfPhysicalGroupsData, 
        healthGroupsData: rfHealthGroupsData, 
        rehabilitationData: rfRehabilitationData, 
        childCountData: rfChildCountData 
    } = useFetchRFCharts({
        year: appliedYear,
        enabled: !!appliedYear && !selectedSectorId
    });

    // Запрос всех данных графиков для выбранного региона (объединенный запрос)
    const { 
        physicalGroupsData: sectorPhysicalGroupsData, 
        healthGroupsData: sectorHealthGroupsData, 
        rehabilitationData: sectorRehabilitationData, 
        childCountData: sectorChildCountData, 
        metricsData: sectorMetricsData 
    } = useFetchSectorCharts({
        year: appliedYear,
        sector_id: selectedSectorId,
        enabled: !!selectedSectorId
    });

    // Выбираем данные в зависимости от того, выбран ли регион
    const physicalGroupsData = selectedSectorId ? sectorPhysicalGroupsData : rfPhysicalGroupsData;
    const healthGroupsData = selectedSectorId ? sectorHealthGroupsData : rfHealthGroupsData;
    const rehabilitationData = selectedSectorId ? sectorRehabilitationData : rfRehabilitationData;
    const childCountData = selectedSectorId ? sectorChildCountData : rfChildCountData;
    const metricsData = selectedSectorId ? sectorMetricsData : rfMetricsData;

    // Получаем название выбранного региона
    const selectedSectorName = useMemo(() => {
        if (!selectedSectorId || !mapSectorsInfoData) return null;
        const sector = mapSectorsInfoData.find(s => s.sectorId === selectedSectorId);
        return sector?.name || null;
    }, [selectedSectorId, mapSectorsInfoData]);

    // Колбек для клика по региону
    const handleSectorClick = (sectorId: string, value: number | null) => {
        dispatch({ type: 'SET_SELECTED_SECTOR_ID', payload: sectorId });
    };

    // Колбек для сброса выбора региона (показать данные по РФ)
    const handleShowRFData = () => {
        dispatch({ type: 'SET_SELECTED_SECTOR_ID', payload: null });
    };

    return (
        <div className={styles.contentWrapper}>
            <div className={styles.wrapper}>
                <Form30DashboardFilter
                    selectedYear={selectedYear}
                    onYearChange={(year) => dispatch({ type: 'SET_SELECTED_YEAR', payload: year })}
                    selectedIndicator={selectedIndicator}
                    onIndicatorChange={(indicator) => dispatch({ type: 'SET_SELECTED_INDICATOR', payload: indicator })}
                    onApply={handleApplyFilters}
                />
                
                {!appliedYear && !appliedIndicator && (
                    <div className={styles.emptyState}>
                        <p>Выберите данные для отображения</p>
                    </div>
                )}

                {isLoading && (
                    <div className={styles.loadingState}>
                        <p>Загрузка данных...</p>
                    </div>
                )}
                
                {isError && (
                    <div className={styles.errorState}>
                        <p>Ошибка при загрузке данных. Попробуйте обновить страницу.</p>
                    </div>
                )}

                {appliedYear && appliedIndicator && !isLoading && !isError && adaptedDashboardData.length === 0 && (
                    <div className={styles.emptyState}>
                        <p>Данных еще нет. Заполните форму 30 для выбранного года.</p>
                    </div>
                )}
                
                {appliedYear && appliedIndicator && parsedIndicator && mapSectorsInfoData && adaptedDashboardData.length > 0 && !isLoading && (
                    <div>
                        <div className={styles.headerRow}>
                            <div className={styles.titleContainer}>
                                <h2>
                                    {selectedSectorId && selectedSectorName 
                                        ? `Регион: ${selectedSectorName}` 
                                        : 'Данные по РФ'}
                                </h2>
                                {selectedSectorId && (
                                    <button
                                        className={`${sButtons.iconButton} ${sButtons.primaryButton}`}
                                        onClick={handleShowRFData}
                                        style={{ marginLeft: '16px' }}
                                    >
                                        Показать данные по РФ
                                    </button>
                                )}
                            </div>
                            <button
                                className={`${sButtons.iconButton} ${sButtons.settingsButton} ${styles.colorSettingBtn}`}
                                onClick={() => dispatch({ type: 'SET_SHOW_COLOR_MODAL', payload: true })}
                            >
                                Настройка цветов
                            </button>
                        </div>

                        <MetricsBlock 
                            data={metricsData}
                            selectedBlock={selectedMetricsBlock}
                            onBlockClick={(blockIndex) => {
                                dispatch({
                                    type: 'SET_SELECTED_METRICS_BLOCK',
                                    payload: blockIndex === -1 ? null : blockIndex
                                });
                            }}
                        />

                        <Form30DashboardMap
                            sectors={mapSectorsInfoData}
                            dashboardData={adaptedDashboardData}
                            onSectorClick={handleSectorClick}
                            title={parsedIndicator.rowName}
                            colorSettings={currentColors}
                        />

                        <div className={styles.fullWidthChartContainer}>
                            <ChildCountChart 
                                data={childCountData}
                                sectorName={selectedSectorId ? selectedSectorName || undefined : 'РФ'}
                            />
                        </div>
                        <div className={styles.chartsContainer}>
                            <PhysicalGroupsChart 
                                data={physicalGroupsData}
                                sectorName={selectedSectorId ? selectedSectorName || undefined : 'РФ'}
                            />
                            <HealthGroupsChart 
                                data={healthGroupsData}
                                sectorName={selectedSectorId ? selectedSectorName || undefined : 'РФ'}
                            />
                            <RehabilitationChart 
                                data={rehabilitationData}
                                sectorName={selectedSectorId ? selectedSectorName || undefined : 'РФ'}
                            />
                        </div>
                    </div>
                )}

                {parsedIndicator && appliedYear && (
                    <Form30ColorModal
                        show={showColorModal}
                        onHide={() => dispatch({ type: 'SET_SHOW_COLOR_MODAL', payload: false })}
                        year={appliedYear}
                        table_id={parsedIndicator.table_id}
                        row_id={parsedIndicator.row_id}
                        column_key={parsedIndicator.column_key}
                        initialColors={currentColors}
                    />
                )}
            </div>
        </div>
    );
};

export default Form30DashboardPage;
